import { ProcessingStatus } from "@/components/ProcessingStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { PipelineService } from "@/gen/omnios/v1/pipeline_connect";
import { transport } from "@/lib/grpc";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createClient } from "@connectrpc/connect";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/background-jobs/")({
	component: BackgroundJobsPage,
});

const client = createClient(PipelineService, transport);

interface PipelineRun {
	id: string;
	pipelineId: string;
	pipelineName: string;
	complete: boolean;
	progressPct: number;
	progressMessage: string;
	startedAt?: { toDate: () => Date };
	completedAt?: { toDate: () => Date };
}

function runState(complete: boolean, pct: number): string {
	if (complete && pct >= 100) return "completed";
	if (complete) return "failed";
	return "running";
}

function BackgroundJobsPage() {
	const { t } = useTranslation("background-jobs");
	useBreadcrumbs([{ label: t("title") }]);
	const navigate = useNavigate();
	const { onGridReady, onStateChanged } = useGridLayout("background-jobs");
	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<PipelineRun>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<PipelineRun>({
		fetchRows: async ({ pageSize, pageToken, search: s }) => {
			const resp = await client.listAllRuns({
				page: { pageSize, pageToken },
				search: s ?? "",
			});
			return {
				rows: (resp.runs ?? []) as PipelineRun[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			progressPct: "progress_pct",
			progressMessage: "progress_message",
			startedAt: "started_at",
			completedAt: "completed_at",
		},
	});

	const cancelMut = useMutation({
		mutationFn: async (runId: string) => {
			await client.cancelPipelineRun({ runId });
		},
		onSuccess: () => refreshGrid(),
	});

	function relativeTime(ts: string | undefined) {
		if (!ts) return "\u2014";
		const d = new Date(ts);
		const diff = Date.now() - d.getTime();
		if (diff < 60_000) return t("just_now");
		if (diff < 3_600_000)
			return t("min_ago", { count: Math.floor(diff / 60_000) });
		if (diff < 86_400_000)
			return t("hours_ago", { count: Math.floor(diff / 3_600_000) });
		return d.toLocaleDateString("ru-RU");
	}

	// ── AG Grid column definitions ──
	const columnDefs = useMemo<ColDef<PipelineRun>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				colId: "task",
				headerName: t("col_task"),
				flex: 2,
				minWidth: 180,
				pinned: "left",
				cellClass: "font-medium",
				filter: false,
				valueGetter: (p) =>
					p.data?.pipelineName || p.data?.pipelineId.slice(0, 8) || "",
			},
			{
				colId: "status",
				headerName: t("col_status"),
				width: 140,
				filter: false,
				cellRenderer: (p: { data: PipelineRun | undefined }) => {
					if (!p.data) return "";
					const state = runState(p.data.complete, p.data.progressPct);
					return (
						<span className="leading-[28px]">
							<ProcessingStatus state={state} />
						</span>
					);
				},
			},
			{
				colId: "progress",
				headerName: t("col_progress"),
				width: 100,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) => p.data?.progressPct ?? 0,
				valueFormatter: (p) => `${p.value}%`,
			},
			{
				colId: "started",
				headerName: t("col_started"),
				width: 140,
				filter: "agDateColumnFilter",
				valueGetter: (p) => p.data?.startedAt?.toDate()?.toISOString(),
				valueFormatter: (p) => relativeTime(p.value),
			},
			{
				colId: "completed",
				headerName: t("col_completed", { defaultValue: "Завершён" }),
				width: 140,
				hide: true,
				filter: "agDateColumnFilter",
				valueGetter: (p) => p.data?.completedAt?.toDate()?.toISOString(),
				valueFormatter: (p) => relativeTime(p.value),
			},
			{
				colId: "duration",
				headerName: t("col_duration"),
				width: 100,
				cellClass: "font-tabular",
				filter: false,
				valueGetter: (p) => {
					const started = p.data?.startedAt?.toDate();
					const completed = p.data?.completedAt?.toDate();
					if (started && completed)
						return ((completed.getTime() - started.getTime()) / 1000).toFixed(
							1,
						);
					if (started && !p.data?.complete) return "…";
					return "\u2014";
				},
				valueFormatter: (p) =>
					p.value && p.value !== "…" && p.value !== "\u2014"
						? `${p.value}s`
						: p.value ?? "\u2014",
			},
			{
				colId: "message",
				headerName: t("col_message"),
				flex: 1,
				minWidth: 150,
				valueGetter: (p) => p.data?.progressMessage || "\u2014",
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: PipelineRun | undefined }) => {
			if (e.data) {
				navigate({
					to: "/background-jobs/$id",
					params: { id: e.data.id },
				});
			}
		},
		[navigate],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("selected_label", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("total_label", { defaultValue: "задач" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					disabled={!hasSelection || cancelMut.isPending}
					onClick={() => {
						for (const row of selectedRows) cancelMut.mutate(row.id);
					}}
				>
					{t("cancel_action")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "background-jobs.csv",
						})
					}
				>
					{t("export", { defaultValue: "Экспорт" })}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact<PipelineRun>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_runs")}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("no_runs_hint")}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading")}...</span>`}
				/>
			</div>
			<ActionBar />
		</>
	);
}
